import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"

export default {
    page: {
        error: (): string => "mx-auto !mt-[5%]"
    },
    heading: {
        container: (): string => "flex flex-col gap-y-[15px] md:gap-y-[20px] pb-[35px] md:pb-[30px]"
    },
    sectionHeadingContainer: (): string => "flex gap-x-[10px] items-center",
    sessionsTab: {
        container: (): string => "self-center md:max-w-[800px] pt-[25px] px-[15px] md:pt-[40px] pb-[100px]"
    },
    summaryTab: {
        container: ({ isEmptyData, isError }: { isEmptyData: boolean; isError: boolean }): string =>
            buildCssClassName(
                "flex",
                "flex-col",
                { "justify-between flex-1": isEmptyData || isError },
                "px-[15px]",
                "md:px-[30px]",
                "xl:px-[80px]",
                "pt-[40px]",
                "pb-[100px]",
                "xxl:max-w-[1800px]",
                "xxl:w-full",
                "xxl:mx-auto"
            ),
        emptyHint: (): string => "mt-0 mb-auto py-[10px] px-[15px] bg-blue-600 rounded-[5px] w-full md:max-w-[800px]",
        rowWrapper: (): string => "flex flex-col",
        row: ({
            isSingleCol,
            withExtraBottomMargin
        }: {
            isSingleCol: boolean
            withExtraBottomMargin: boolean
        }): string =>
            buildCssClassName(
                "flex flex-col-reverse",
                isSingleCol
                    ? [
                          "gap-y-[20px]",
                          "self-center",
                          "w-full",
                          "xl:max-w-[800px]",
                          "items-center",
                          withExtraBottomMargin ? "mb-[60px]" : "mb-[20px]"
                      ]
                    : [
                          "gap-y-[60px]",
                          "place-items-center",
                          "xl:place-items-start",
                          "xl:gap-y-[0px]",
                          "xl:grid",
                          "xl:grid-cols-2",
                          "xl:gap-x-[60px]",
                          "mb-[60px]"
                      ]
            )
    },
    entities: {
        summary: {
            container: ({ isVisible }: { isVisible: boolean }): string =>
                buildCssClassName(isVisible ? "flex flex-col gap-y-[20px]" : "hidden"),
            blocks: {
                container: (): string => "relative",
                list: {
                    container: (): string => "flex flex-col gap-y-[10px] list-decimal list-outside pl-[20px]",
                    item: ({ isStub }: { isStub: boolean }): string =>
                        buildCssClassName(isStub && "blur-[15px]", getTransitionTwClasses("all", 100)),
                    text: (): string => "inline"
                }
            },
            loader: (): string => "px-[20px] pt-[20px] pb-[10px] rounded-[10px] bg-white/20"
        },
        individuals: {
            section: {
                container: "flex flex-col gap-y-[20px] mb-[60px]",
                list: "flex flex-col gap-y-[5px]"
            },
            row: {
                header: {
                    container: (): string =>
                        buildCssClassName(
                            "group",
                            "w-full",
                            "flex",
                            "justify-between",
                            "py-[10px]",
                            "px-[15px]",
                            "relative",
                            getTransitionTwClasses("all", 200)
                        ),
                    icon: (): string => "mr-[15px]",
                    leftCol: {
                        container: ({
                            isLoading,
                            isDataEmpty,
                            isError,
                            isVisible
                        }: {
                            isLoading: boolean
                            isDataEmpty: boolean
                            isError: boolean
                            isVisible: boolean
                        }): string =>
                            buildCssClassName(
                                "flex gap-x-[15px] items-center",
                                {
                                    "opacity-50 contrast-75 brightness-100":
                                        isLoading || isDataEmpty || isError || !isVisible
                                },
                                getTransitionTwClasses("all", 150)
                            ),
                        profile: (): string => "flex gap-x-[10px]",
                        name: (): string => "w-[155px] truncate",
                        excerpt: ({ isExpanded }: { isExpanded: boolean }): string =>
                            buildCssClassName(
                                "px-[10px]",
                                "py-[0px]",
                                "gap-x-[5px]",
                                "bg-gray-500",
                                "rounded-[10px]",
                                "pointer-events-none",
                                [
                                    isExpanded
                                        ? [
                                              "[background-color:transparent]",
                                              "group-hover:bg-transparent",
                                              "group-focus:bg-transparent"
                                          ]
                                        : ["bg-blue-500", "group-hover:bg-blue-500", "group-focus:bg-blue-500"]
                                ],
                                getTransitionTwClasses("colors", 100)
                            ),
                        excerptBar: (): string => "!p-[0px] !h-[50px] !min-h-[55px] mt-[5px]",
                        moduleName: (): string => "flex flex-col"
                    },
                    rightCol: (): string =>
                        buildCssClassName(
                            "absolute",
                            "right-[0px]",
                            "top-[30px]",
                            "sm:relative",
                            "sm:right-[0px]",
                            "sm:top-[0px]",
                            "flex",
                            "items-center"
                        )
                },
                content: {
                    wrapper: (): string =>
                        buildCssClassName(
                            "px-[5px]",
                            "md:px-[20px]",
                            "py-[30px]",
                            "flex",
                            "flex-col",
                            "gap-y-[15px]",
                            "md:justify-between",
                            "md:w-full",
                            "md:flex-row",
                            "md:gap-x-[40px]",
                            "md:gap-y-[0px]"
                        ),
                    moduleSummary: {
                        container: (): string => "md:ml-[10px] flex flex-col gap-y-[10px]",
                        titleWrapper: (): string => "flex md:flex-row gap-[20px] items-center",
                        worksButton: (): string =>
                            buildCssClassName(
                                "py-[8px]",
                                "px-[10px]",
                                "rounded-[30px]",
                                "!outline-none",
                                "bg-blue-200",
                                "hover:bg-accent",
                                "focus-visible:bg-accent",
                                "leading-[18px]",
                                "text-[12px]",
                                "text-white",
                                "font-[300]",
                                "flex",
                                "items-center",
                                "justify-center",
                                getTransitionTwClasses("all", 200)
                            ),
                        list: (): string => "flex flex-col gap-y-[10px] list-decimal list-outside",
                        listItem: ({ isInline }: { isInline: boolean }): string =>
                            buildCssClassName(isInline ? "inline" : "flex flex-col gap-y-[5px]")
                    },
                    moduleProgress: {
                        container: (): string =>
                            buildCssClassName(
                                "flex",
                                "flex-col",
                                "gap-y-[20px]",
                                "mt-[10px]",
                                "pt-[15px]",
                                "px-[20px]",
                                "pb-[20px]",
                                "rounded-[10px]",
                                "bg-blue-600",
                                "md:self-start",
                                "md:basis-[220px]", // TODO: recheck
                                "md:[flex-grow:0]",
                                "md:shrink-0",
                                "md:flex-1"
                            ),
                        wrapper: (): string => "flex flex-col gap-y-[10px]",
                        excerpt: (): string => "py-[5px] bg-transparent ml-[-5px] gap-x-[5px]",
                        excerptBar: (): string =>
                            "[pointer-events:none] [cursor:default] !p-[0px] !h-[50px] !min-h-[55px] mt-[5px]"
                    }
                }
            }
        }
    }
}
